import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Building2, Users, Store, Activity, Ban, CheckCircle2, Trash2, Clock, Eye, DollarSign,
  LogIn, KeyRound, LifeBuoy,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const ROLES = ["company_admin", "supervisor", "front_desk_agent", "warehouse_agent"];

export default function Platform() {
  const { impersonate } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [signups, setSignups] = useState([]);
  const [detail, setDetail] = useState(null);
  const [detailUsers, setDetailUsers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");

  const load = useCallback(async () => {
    try {
      const [s, c, sg, t] = await Promise.all([
        api.get("/platform/stats"),
        api.get("/platform/companies"),
        api.get("/platform/signups"),
        api.get("/platform/tickets"),
      ]);
      setStats(s.data);
      setRows(c.data.companies);
      setSignups(sg.data.points.map((p) => ({ ...p, label: p.date.slice(5) })));
      setTickets(t.data.tickets);
    } catch (e) { toast.error(apiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, is_active) => {
    try { await api.patch(`/platform/companies/${id}/status`, { is_active });
      toast.success(is_active ? "Company reactivated" : "Company suspended"); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const approve = async (id, is_approved) => {
    try { await api.patch(`/platform/companies/${id}/approve`, { is_approved });
      toast.success(is_approved ? "Company approved" : "Approval revoked"); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id, name) => {
    if (!window.confirm(`Delete ${name} and ALL its data (stores, staff, transactions)? This cannot be undone.`)) return;
    try { await api.delete(`/platform/companies/${id}`); toast.success("Company deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const openDetail = async (id) => {
    try {
      const [d, u] = await Promise.all([
        api.get(`/platform/companies/${id}`),
        api.get(`/platform/companies/${id}/users`),
      ]);
      setDetail(d.data);
      setDetailUsers(u.data.users);
    } catch (e) { toast.error(apiError(e)); }
  };

  const refreshDetailUsers = async () => {
    if (!detail) return;
    try { const u = await api.get(`/platform/companies/${detail.id}/users`); setDetailUsers(u.data.users); }
    catch { /* ignore */ }
  };

  const toggleUser = async (uid, is_active) => {
    try { await api.patch(`/platform/users/${uid}`, { is_active }); refreshDetailUsers(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const changeRole = async (uid, role) => {
    try { await api.patch(`/platform/users/${uid}`, { role }); toast.success("Role updated"); refreshDetailUsers(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const resetPw = async (uid, email) => {
    if (!window.confirm(`Reset password for ${email}? A new temporary password will be generated.`)) return;
    try {
      const { data } = await api.post(`/platform/users/${uid}/reset-password`);
      try { await navigator.clipboard.writeText(data.temp_password); } catch { /* ignore */ }
      toast.success(`Temp password for ${email}: ${data.temp_password} (copied)`, { duration: 30000 });
    } catch (e) { toast.error(apiError(e)); }
  };

  const doImpersonate = async (id) => {
    try {
      const data = await impersonate(id);
      toast.success(`Now viewing ${data.company_name}`);
      navigate("/admin");
    } catch (e) { toast.error(apiError(e)); }
  };

  const resolveTicket = async (id) => {
    try { await api.patch(`/platform/tickets/${id}`, { status: "resolved" }); toast.success("Marked resolved"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const sendReply = async () => {
    if (!replyText.trim()) { toast.error("Write a reply first"); return; }
    try {
      await api.patch(`/platform/tickets/${replyFor.id}`, { reply: replyText.trim(), status: "resolved" });
      toast.success("Reply sent to customer"); setReplyFor(null); setReplyText(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const cards = stats ? [
    ["Companies", stats.total_companies, Building2],
    ["Pending", stats.pending_approvals, Clock],
    ["Open Tickets", stats.open_tickets ?? 0, LifeBuoy],
    ["Active", stats.active_companies, CheckCircle2],
    ["Suspended", stats.suspended_companies, Ban],
    ["MRR", `$${(stats.mrr || 0).toFixed(2)}`, DollarSign],
    ["Total Staff", stats.total_users, Users],
    ["Total Stores", stats.total_stores, Store],
  ] : [];

  const openTickets = tickets.filter((t) => t.status === "open");

  return (
    <DashboardLayout title="Platform Admin" needsStore={false}>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
        {cards.map(([label, val, Icon]) => (
          <div key={label} className="bg-card border border-border p-4" data-testid={`stat-${label.toLowerCase().replace(/[^a-z0-9]/g, "")}`}>
            <Icon size={18} className="text-muted-foreground" />
            <div className="font-display text-2xl font-extrabold mt-1">{val}</div>
            <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="companies">
        <TabsList className="mb-4">
          <TabsTrigger value="companies" data-testid="tab-companies"><Building2 size={14} className="mr-1" /> Companies</TabsTrigger>
          <TabsTrigger value="support" data-testid="tab-support">
            <LifeBuoy size={14} className="mr-1" /> Support {openTickets.length > 0 && <span className="ml-1 text-xs bg-alert-amber text-black rounded-full px-1.5">{openTickets.length}</span>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="companies">
          <div className="bg-card border border-border p-4 mb-6" data-testid="signups-chart">
            <div className="text-xs text-muted-foreground uppercase tracking-wide mb-3">Signups — last 30 days</div>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={signups} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} interval={4} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                <Line type="monotone" dataKey="count" stroke="hsl(var(--alert-green))" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-card border border-border overflow-x-auto">
            <table className="w-full text-sm" data-testid="platform-companies-table">
              <thead>
                <tr className="text-left uppercase text-xs text-muted-foreground border-b border-border">
                  <th className="px-3 py-2">Company</th><th className="px-3 py-2">Code</th><th className="px-3 py-2">Contact</th>
                  <th className="px-3 py-2">Signed Up</th><th className="px-3 py-2">Stores</th><th className="px-3 py-2">Price/mo</th>
                  <th className="px-3 py-2">Staff</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id} className="border-b border-border hover:bg-secondary/40 transition-colors" data-testid={`company-row-${c.company_code}`}>
                    <td className="px-3 py-3 font-semibold">
                      <button className="hover:underline text-left" onClick={() => openDetail(c.id)} data-testid={`view-${c.company_code}`}>{c.company_name}</button>
                    </td>
                    <td className="px-3 py-3 font-mono-t">{c.company_code}</td>
                    <td className="px-3 py-3">{c.contact_email || "—"}</td>
                    <td className="px-3 py-3 text-xs">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
                    <td className="px-3 py-3">{c.stores}</td>
                    <td className="px-3 py-3" data-testid={`price-${c.company_code}`}>
                      <span className="font-semibold">${(c.monthly_price ?? 0).toFixed(2)}</span>
                      <span className="block text-xs text-muted-foreground">{c.price_label}</span>
                    </td>
                    <td className="px-3 py-3">{c.staff}</td>
                    <td className="px-3 py-3">
                      {!c.is_approved
                        ? <span className="text-xs px-2 py-1 rounded-full bg-alert-amber/15 text-alert-amber">Pending</span>
                        : <span className={`text-xs px-2 py-1 rounded-full ${c.is_active ? "bg-alert-green/15 text-alert-green" : "bg-alert-red/15 text-alert-red"}`}>
                            {c.is_active ? "Active" : "Suspended"}
                          </span>}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex gap-1 flex-wrap">
                        {!c.is_approved
                          ? <Button size="sm" variant="outline" className="gap-1 h-8" onClick={() => approve(c.id, true)} data-testid={`approve-${c.company_code}`}><CheckCircle2 size={13} /> Approve</Button>
                          : (c.is_active
                              ? <Button size="sm" variant="outline" className="gap-1 h-8" onClick={() => setStatus(c.id, false)} data-testid={`suspend-${c.company_code}`}><Ban size={13} /> Suspend</Button>
                              : <Button size="sm" variant="outline" className="gap-1 h-8" onClick={() => setStatus(c.id, true)} data-testid={`activate-${c.company_code}`}><CheckCircle2 size={13} /> Activate</Button>)}
                        <Button size="sm" variant="ghost" className="gap-1 h-8" title="Log in as this company" onClick={() => doImpersonate(c.id)} data-testid={`impersonate-${c.company_code}`}><LogIn size={13} /></Button>
                        <Button size="sm" variant="ghost" className="gap-1 h-8" title="View details" onClick={() => openDetail(c.id)} data-testid={`detail-${c.company_code}`}><Eye size={13} /></Button>
                        <Button size="sm" variant="ghost" className="gap-1 h-8 text-alert-red hover:text-alert-red" onClick={() => remove(c.id, c.company_name)} data-testid={`delete-${c.company_code}`}><Trash2 size={13} /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={9} className="text-center text-muted-foreground py-8">No companies have signed up yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="support">
          <div className="space-y-3" data-testid="support-inbox">
            {tickets.length === 0 && <div className="text-center text-muted-foreground py-10 bg-card border border-border">No support requests yet.</div>}
            {tickets.map((t) => (
              <div key={t.id} className="bg-card border border-border p-4" data-testid={`ticket-${t.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{t.subject}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${t.status === "open" ? "bg-alert-amber/15 text-alert-amber" : "bg-alert-green/15 text-alert-green"}`}>{t.status}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {t.company_name || "—"} · {t.user_name} ({t.user_email}) · {t.created_at ? new Date(t.created_at).toLocaleString() : ""}
                    </div>
                    <p className="text-sm mt-2 whitespace-pre-wrap">{t.message}</p>
                    {t.reply && <div className="text-sm mt-2 border-l-2 border-alert-green pl-3 text-muted-foreground whitespace-pre-wrap"><b className="text-white">Your reply:</b> {t.reply}</div>}
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <Button size="sm" variant="outline" className="h-8" onClick={() => { setReplyFor(t); setReplyText(""); }} data-testid={`reply-${t.id}`}>Reply</Button>
                    {t.status === "open" && <Button size="sm" variant="ghost" className="h-8" onClick={() => resolveTicket(t.id)} data-testid={`resolve-${t.id}`}>Resolve</Button>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Company detail + user management */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="company-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap">
                  {detail.company_name}
                  <span className="text-xs px-2 py-0.5 rounded-full bg-alert-green/15 text-alert-green">${(detail.monthly_price ?? 0).toFixed(2)}/mo · {detail.price_label}</span>
                  <Button size="sm" variant="outline" className="ml-auto gap-1 h-8" onClick={() => doImpersonate(detail.id)} data-testid="detail-impersonate-btn"><LogIn size={13} /> Log in as company</Button>
                </DialogTitle>
                <DialogDescription>Manage this customer's staff, reset passwords, or open their account in support mode.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div><div className="text-xs text-muted-foreground uppercase">Code</div><div className="font-mono-t">{detail.company_code}</div></div>
                  <div><div className="text-xs text-muted-foreground uppercase">Signed Up</div><div>{detail.created_at ? new Date(detail.created_at).toLocaleString() : "—"}</div></div>
                  <div><div className="text-xs text-muted-foreground uppercase">Contact</div><div>{detail.contact_email || "—"}</div></div>
                  <div><div className="text-xs text-muted-foreground uppercase">Status</div><div>{!detail.is_approved ? "Pending approval" : detail.is_active ? "Active" : "Suspended"}</div></div>
                  <div><div className="text-xs text-muted-foreground uppercase">Transactions</div><div>{detail.transactions}</div></div>
                  <div><div className="text-xs text-muted-foreground uppercase">Stores</div><div>{detail.stores.length}</div></div>
                </div>

                <div>
                  <div className="text-xs text-muted-foreground uppercase mb-2">Staff / users ({detailUsers.length})</div>
                  <div className="border border-border rounded max-h-72 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="text-left text-muted-foreground border-b border-border">
                        <th className="px-2 py-1.5">Name</th><th className="px-2 py-1.5">Email</th><th className="px-2 py-1.5">Role</th><th className="px-2 py-1.5">Active</th><th className="px-2 py-1.5">Actions</th>
                      </tr></thead>
                      <tbody>
                        {detailUsers.map((u) => (
                          <tr key={u.id} className="border-b border-border last:border-0" data-testid={`user-row-${u.email}`}>
                            <td className="px-2 py-1.5">{u.full_name}</td>
                            <td className="px-2 py-1.5 font-mono-t">{u.email}</td>
                            <td className="px-2 py-1.5">
                              <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                                <SelectTrigger className="h-7 w-40 text-xs" data-testid={`role-${u.email}`}><SelectValue /></SelectTrigger>
                                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r} className="text-xs">{r.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                              </Select>
                            </td>
                            <td className="px-2 py-1.5">
                              <button onClick={() => toggleUser(u.id, !u.is_active)} data-testid={`toggle-${u.email}`}
                                className={`px-2 py-0.5 rounded-full ${u.is_active ? "bg-alert-green/15 text-alert-green" : "bg-alert-red/15 text-alert-red"}`}>
                                {u.is_active ? "Active" : "Off"}
                              </button>
                            </td>
                            <td className="px-2 py-1.5">
                              <Button size="sm" variant="ghost" className="h-7 gap-1" onClick={() => resetPw(u.id, u.email)} data-testid={`resetpw-${u.email}`}><KeyRound size={12} /> Reset PW</Button>
                            </td>
                          </tr>
                        ))}
                        {detailUsers.length === 0 && <tr><td colSpan={5} className="px-2 py-3 text-center text-muted-foreground">No users</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reply dialog */}
      <Dialog open={!!replyFor} onOpenChange={(o) => !o && setReplyFor(null)}>
        <DialogContent data-testid="ticket-reply-dialog">
          <DialogHeader>
            <DialogTitle>Reply to {replyFor?.user_name}</DialogTitle>
            <DialogDescription>Your reply will be emailed to the customer and the ticket marked resolved.</DialogDescription>
          </DialogHeader>
          {replyFor && (
            <div className="space-y-3 text-sm">
              <div className="bg-secondary/40 border border-border p-3 rounded">
                <div className="font-semibold">{replyFor.subject}</div>
                <p className="text-muted-foreground whitespace-pre-wrap mt-1">{replyFor.message}</p>
              </div>
              <Textarea placeholder="Type your reply — it will be emailed to the customer…" rows={5} value={replyText}
                onChange={(e) => setReplyText(e.target.value)} data-testid="reply-text" />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setReplyFor(null)}>Cancel</Button>
                <Button onClick={sendReply} data-testid="send-reply-btn">Send Reply & Resolve</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
